import React, { Component, ReactNode } from 'react'

type ErrorBoundaryState = {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  showDetails: boolean
}

type ErrorBoundaryProps = {
  children: ReactNode
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null, showDetails: false }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // TODO: hook up to your logging/observability if desired.
    console.error('Website Error Boundary caught:', error, errorInfo)
    this.setState({ errorInfo })
  }

  handleRetry = () => {
    // Reset local error state and try rendering children again.
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false })
  }

  handleFullReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    } else {
      this.handleRetry()
    }
  }

  handleToggleDetails = () => {
    this.setState((prev) => ({ ...prev, showDetails: !prev.showDetails }))
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="relative min-h-screen bg-black text-white flex items-center justify-center px-4">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0,rgba(212,175,55,0.18),transparent_55%),radial-gradient(circle_at_80%_100%,rgba(74,31,184,0.35),transparent_55%)]" />
          <div className="relative z-10 w-full max-w-xl">
            <div className="mx-auto max-w-lg rounded-3xl border border-[#2f2f2f] bg-gradient-to-br from-black/90 via-[#111]/95 to-black/90 shadow-[0_24px_80px_rgba(0,0,0,0.85)] p-8 sm:p-10">
              <div className="flex flex-col items-center text-center">
                <img
                  src="/img/logo.png"
                  alt="Binyuma TV"
                  className="w-12 h-12 md:w-14 md:h-14 mb-4 object-contain drop-shadow-[0_0_18px_rgba(212,175,55,0.8)]"
                />
                <h1 className="text-2xl md:text-3xl font-bold mb-3 bg-gradient-to-r from-[#D4AF37] via-[#FFFFFF] to-[#D4AF37] bg-clip-text text-transparent">
                  BinyumaTV hit a snag
                </h1>
                <p className="text-sm md:text-base text-gray-300 mb-2">
                  Something unexpected happened while loading this page.
                </p>
                <p className="text-xs md:text-sm text-gray-500 mb-8 max-w-md">
                  Our team automatically receives error signals so we can fix issues quickly. You can try again
                  or reload the page to continue watching.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
                <button
                  type="button"
                  onClick={this.handleRetry}
                  className="inline-flex items-center justify-center rounded-full border border-gray-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-white/5 transition"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={this.handleFullReload}
                  className="inline-flex items-center justify-center rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-black hover:bg-[#e8c45b] transition shadow-[0_0_24px_rgba(212,175,55,0.55)]"
                >
                  Reload page
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] md:text-xs text-gray-500">
                <p className="text-center sm:text-left">
                  If this keeps happening, you can contact support and mention the error details.
                </p>
                <button
                  type="button"
                  onClick={this.handleToggleDetails}
                  className="mx-auto sm:mx-0 inline-flex items-center gap-1 text-[11px] md:text-xs font-medium text-[#D4AF37] hover:text-[#ffe38a] hover:underline underline-offset-2"
                >
                  {this.state.showDetails ? 'Hide advanced info' : 'Show advanced info'}
                </button>
              </div>
            </div>
          </div>

          {this.state.showDetails && (
            <div
              className="fixed inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
              role="dialog"
              aria-modal="true"
              aria-label="Advanced error details"
            >
              <div className="w-full max-w-3xl rounded-2xl bg-[#050505] border border-gray-800 shadow-[0_24px_80px_rgba(0,0,0,0.9)]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
                  <div>
                    <p className="text-sm font-semibold text-white">Advanced error details</p>
                    <p className="text-[11px] text-gray-500">
                      For debugging only. This information may look technical.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={this.handleToggleDetails}
                    className="ml-4 inline-flex items-center justify-center rounded-full border border-gray-700 px-3 py-1.5 text-[11px] font-medium text-gray-200 hover:bg-white/5"
                  >
                    Close
                  </button>
                </div>
                <div className="max-h-[60vh] overflow-auto p-5 text-xs font-mono bg-black/60 text-gray-200">
                  {this.state.error && (
                    <div className="mb-4">
                      <p className="mb-1 text-[11px] font-semibold text-gray-400">Error message</p>
                      <pre className="whitespace-pre-wrap break-words rounded-lg bg-[#111]/80 p-3 border border-gray-800">
                        {this.state.error.name}: {this.state.error.message}
                      </pre>
                    </div>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <p className="mb-1 text-[11px] font-semibold text-gray-400">Component stack</p>
                      <pre className="whitespace-pre-wrap break-words rounded-lg bg-[#111]/80 p-3 border border-gray-800">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                  {!this.state.error && !this.state.errorInfo && (
                    <p className="text-gray-500">No additional error details are available.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

